import React, { useState } from "react";
import axios from "axios";
import { Building2, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

const CustomerOnboardingDialog = ({ customer, onComplete }) => {
  const [firmName, setFirmName] = useState("");
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const firm = firmName.trim();
    const cityName = city.trim();

    if (!firm) {
      toast.error("Please enter your firm name.");
      return;
    }

    if (!cityName) {
      toast.error("Please enter your city.");
      return;
    }

    const token = localStorage.getItem("partvision_token");

    if (!token) {
      toast.error("Your session has expired. Please login again.");
      return;
    }

    setSaving(true);

    try {
      const response = await axios.put(
        `${API}/customer-auth/profile`,
        {
          firm_name: firm,
          city: cityName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Profile saved successfully.");
      onComplete(response.data);
    } catch (error) {
      console.error("Customer profile update failed:", error);

      toast.error(
        error.response?.data?.detail ||
          "Unable to save your details. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 py-4">
      <div
        className="w-full max-w-lg max-h-[90dvh] flex flex-col bg-card border rounded-2xl shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        <div className="overflow-y-auto p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:p-8 sm:pb-8">

          <div className="text-center mb-6">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mb-4">
              <Building2 className="w-7 h-7 text-brand" />
            </div>

            <h2
              id="onboarding-title"
              className="text-2xl font-heading font-black"
            >
              🚜 Welcome to PartVision AI
            </h2>

            <p className="text-sm font-semibold text-brand mt-1">
              Find Mahindra Tractor Parts by Image
            </p>
          </div>

          <div className="rounded-xl bg-secondary/60 border p-4 mb-6">
            <div className="space-y-3 text-sm">

              <div>📸 किसी भी Mahindra tractor part की <b>photo upload करें</b></div>

              <div>🔍 <b>Search</b> दबाएँ और मिलते-जुलते parts देखें</div>

              <div>🔢 <b>Part Number और details</b> देखकर सही part identify करें</div>

              <div>
                ❗ <b>अगर सही result नहीं मिलता है</b>, तो
                <b> Similarity % को कम करके</b> दोबारा search कर सकते हैं
                और ज्यादा similar parts देख सकते हैं।
              </div>

              <div>
                ✂️ Photo का background बहुत busy हो तो
                <b> Remove Background</b> का इस्तेमाल करें।
              </div>

            </div>

            <div className="text-center text-xs text-muted-foreground mt-4 pt-3 border-t">
              <b>पहली बार इस्तेमाल कर रहे हैं?</b> बस photo डालिए और Search कीजिए।
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">

            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Firm Name / फर्म का नाम
              </label>

              <div className="flex items-center mt-2 border rounded-md overflow-hidden">
                <div className="px-3 text-muted-foreground">
                  <Building2 className="w-4 h-4" />
                </div>

                <input
                  type="text"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  placeholder="Enter Firm Name / फर्म का नाम"
                  className="flex-1 px-2 py-3 bg-transparent outline-none text-sm"
                  autoFocus
                  disabled={saving}
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                City / शहर
              </label>

              <div className="flex items-center mt-2 border rounded-md overflow-hidden">
                <div className="px-3 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                </div>

                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Enter City / शहर का नाम"
                  className="flex-1 px-2 py-3 bg-transparent outline-none text-sm"
                  disabled={saving}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full h-11 rounded-md bg-foreground text-background font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Continue to PartVision
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-center text-[11px] text-muted-foreground">
              Mobile: +{customer?.mobile?.replace(/\D/g, "")}
            </p>

          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerOnboardingDialog;
